import { EMPTY, Observable, concat, of } from 'rxjs';
import { SlackMessageId, SlackMessagePermalink, SlackMessageSubType, SlackMessageText, SlackMessageTs, SlackMessageType } from '../value';
import { catchError, filter, map, mergeMap, take, tap, toArray } from 'rxjs/operators';

import { FirestoreQueryBuilder } from '../../../../lib/gcp/builder/firestore-query.builder';
import { ISlackMessageRepository } from '../repository';
import { SlackChannelId } from '../../slack-channel/value';
import { SlackMessage } from '../entity';
import { SlackMessageAlreadyExistsError } from '../exception';
import { SlackMessageDto } from '../dto';
import { SlackMessageNotFoundError } from '../exception';
import { SlackUserId } from '../../slack-user/value';
import { Timestamp } from '../../../../utility/model/timestamp.value';

export class SlackMessageDomainService {
  constructor(private readonly slackMessageRepository: ISlackMessageRepository) { }

  generateSlackMessageId(): string {
    return this.slackMessageRepository.generateId().value;
  }

  select(id: string) {
    return this.slackMessageRepository.select(SlackMessageId.create(id)).pipe(
      map(item => this.convertDto(item)),
      catchError(_ => of(null))
    );
  }

  insertSlackMessage(
    id: string,
    channelId: string,
    userId: string,
    registeredBy: string,
    ts: string,
    type: string,
    subtype: string,
    text: string,
    permalink: string
  ): Observable<SlackMessageDto> {
    return concat(
      this.slackMessageRepository.selectAll(
        new FirestoreQueryBuilder<SlackMessage>().equalWhere('channelId', channelId).equalWhere('userId', userId).equalWhere('ts', ts)
      ),
      this.slackMessageRepository.select(SlackMessageId.create(id)).pipe(
        take(1),
        catchError(_ => EMPTY)
      ),
      of(null)
    ).pipe(
      take(1),
      tap(slackMessage => {
        if (slackMessage !== null) {
          throw new SlackMessageAlreadyExistsError('this message aleady exists', (slackMessage as SlackMessage).id.value);
        }
      }),
      map(_ => {
        const slackMessage = new SlackMessage(SlackMessageId.create(id));
        slackMessage.channelId = SlackChannelId.create(channelId);
        slackMessage.userId = SlackUserId.create(userId);
        slackMessage.registeredBy = SlackUserId.create(registeredBy);
        slackMessage.ts = SlackMessageTs.create(ts);
        slackMessage.type = SlackMessageType.create(type);
        slackMessage.subtype = SlackMessageSubType.create(subtype);
        slackMessage.text = SlackMessageText.create(text);
        slackMessage.permalink = SlackMessagePermalink.create(permalink);
        return slackMessage;
      }),
      mergeMap(slackMessage => this.slackMessageRepository.insert(slackMessage)),
      map(item => this.convertDto(item))
    );
  }

  selectSlackMessageByOriginChannelIdAndOriginUserIdAndOriginTs(channelId: string, userId: string, ts: string) {
    return concat(
      this.slackMessageRepository.selectAll(
        new FirestoreQueryBuilder<SlackMessage>().equalWhere('channelId', channelId).equalWhere('userId', userId).equalWhere('ts', ts)
      ),
      of(null)
    ).pipe(
      take(1),
      tap(slackMessage => {
        if (slackMessage === null) {
          throw new SlackMessageNotFoundError('message is not found');
        }
      }),
      map(item => this.convertDto(item))
    );
  }

  selectAllSlackMessageByChannelIDAndUserID(channelID: string, userID: string) {
    return concat(
      this.slackMessageRepository.selectAll(
        new FirestoreQueryBuilder<SlackMessage>().equalWhere('channelId', channelID).equalWhere('userId', userID)
      ),
      of(null)
    ).pipe(
      tap(slackMessage => {
        if (slackMessage === null) {
          throw new SlackMessageNotFoundError('message is not found');
        }
      }),
      map(item => this.convertDto(item))
    );
  }

  selectAllSlackMessageByChannelIdAndText(channel_id: string, keyword?: string) {
    return this.slackMessageRepository.selectAllV1(new FirestoreQueryBuilder<SlackMessage>().equalWhere('channelId', channel_id).limit(10000)).pipe(
      filter(msg => msg.text.value.includes(keyword)),
      map(item => this.convertDto(item))
    );
  }

  selectSlackMessageByTs(ts: string) {
    return concat(this.slackMessageRepository.selectAll(new FirestoreQueryBuilder<SlackMessage>().equalWhere('ts', ts)), of(null)).pipe(
      take(1),
      tap(slackMessage => {
        if (slackMessage === null) {
          throw new SlackMessageNotFoundError('message is not found');
        }
      }),
      map(item => this.convertDto(item))
    );
  }

  selectAll() {
    return this.slackMessageRepository.selectAll(new FirestoreQueryBuilder<SlackMessage>()).pipe(map(item => this.convertDto(item)));
  }

  selectAllSlackMessageByRegisterBy(userId: string) {
    return concat(
      this.slackMessageRepository.selectAll(
        new FirestoreQueryBuilder<SlackMessage>().equalWhere('registeredBy', userId)
      ),
      of(null)
    ).pipe(
      // tap(slackMessage => {
      //   if (slackMessage === null) {
      //     throw new SlackMessageNotFoundError('message is not found');
      //   }
      // }),
      filter(slackMessage => slackMessage !== null),
      map(item => this.convertDto(item)),
      toArray(),
    );
  }

  selectAllSlackMessageBySendBy(userId: string) {
    return concat(
      this.slackMessageRepository.selectAll(
        new FirestoreQueryBuilder<SlackMessage>().equalWhere('userId', userId)
      ),
      of(null)
    ).pipe(
      // tap(slackMessage => {
      //   if (slackMessage === null) {
      //     throw new SlackMessageNotFoundError('message is not found');
      //   }
      // }),
      filter(slackMessage => slackMessage !== null),
      map(item => this.convertDto(item)),
      toArray()
    );
  }

  updateSlackMessage(slackMessageDto: SlackMessageDto) {
    const slackMessage = new SlackMessage(SlackMessageId.create(slackMessageDto.id));
    slackMessage.channelId = SlackChannelId.create(slackMessageDto.channelId);
    slackMessage.userId = SlackUserId.create(slackMessageDto.userId);
    slackMessage.registeredBy = SlackUserId.create(slackMessageDto.registeredBy);
    slackMessage.ts = SlackMessageTs.create(slackMessageDto.ts);
    slackMessage.type = SlackMessageType.create(slackMessageDto.type);
    slackMessage.subtype = SlackMessageSubType.create(slackMessageDto.subtype);
    slackMessage.text = SlackMessageText.create(slackMessageDto.text);
    slackMessage.permalink = SlackMessagePermalink.create(slackMessageDto.permalink);
    slackMessage.createdAt = Timestamp.createByDate(slackMessageDto.createdAt);
    return this.slackMessageRepository.update(slackMessage).pipe(map(item => this.convertDto(item)));
  }

  deleteSlackMessage(slackMessageId: string) {
    return this.slackMessageRepository.delete(SlackMessageId.create(slackMessageId));
  }

  private convertDto(slackMessage: SlackMessage): SlackMessageDto {
    return SlackMessage.allFields.reduce((p, key) => {
      if (slackMessage[key] === undefined) {
        return p;
      }
      const value = slackMessage[key] as { value: any };
      p[key] = value.value;
      return p;
    }, {} as SlackMessageDto);
  }
}
